import { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  FileSignature, 
  Plus, 
  Trash2, 
  Eye,
  Calendar,
  User,
  Pill,
  Printer,
  X
} from 'lucide-react';
import { createPrescription, getPrescriptions, getPrescription } from '@/services/api';
import { toast } from 'sonner';

export default function PrescriptionsPage() {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  
  // Form states
  const [patientName, setPatientName] = useState('');
  const [patientAge, setPatientAge] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [instructions, setInstructions] = useState('');
  const [doctorName, setDoctorName] = useState('');
  const [medications, setMedications] = useState([{ name: '', dosage: '', frequency: '', duration: '' }]);
  
  const printRef = useRef(null);

  useEffect(() => {
    loadPrescriptions();
  }, []);

  const loadPrescriptions = async () => {
    try {
      const data = await getPrescriptions();
      setPrescriptions(data);
    } catch (error) {
      console.error('Error loading prescriptions:', error);
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!patientName.trim() || !diagnosis.trim() || !doctorName.trim()) {
      toast.error('Please fill in required fields');
      return;
    }

    const validMeds = medications.filter(m => m.name.trim());
    if (validMeds.length === 0) {
      toast.error('Please add at least one medication');
      return;
    }

    setCreating(true);
    try {
      await createPrescription({
        patient_name: patientName,
        patient_age: parseInt(patientAge) || 0,
        diagnosis,
        medications: validMeds,
        instructions,
        doctor_name: doctorName
      });
      toast.success('Prescription created successfully!');
      setDialogOpen(false);
      resetForm();
      await loadPrescriptions();
    } catch (error) {
      console.error('Create prescription error:', error);
      toast.error('Failed to create prescription');
    } finally {
      setCreating(false);
    }
  };

  const addMedication = () => {
    setMedications([...medications, { name: '', dosage: '', frequency: '', duration: '' }]);
  };

  const removeMedication = (index) => {
    setMedications(medications.filter((_, i) => i !== index));
  };

  const updateMedication = (index, field, value) => {
    const updated = [...medications];
    updated[index][field] = value;
    setMedications(updated);
  };

  const resetForm = () => {
    setPatientName('');
    setPatientAge('');
    setDiagnosis('');
    setInstructions('');
    setDoctorName('');
    setMedications([{ name: '', dosage: '', frequency: '', duration: '' }]);
  };

  const viewPrescription = (prescription) => {
    setSelectedPrescription(prescription);
    setViewDialogOpen(true);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Prescription</title>');
    printWindow.document.write('<style>body{font-family:Arial,sans-serif;padding:40px;}.header{text-align:center;border-bottom:2px solid #1e3a8a;padding-bottom:20px;margin-bottom:20px;}.med-table{width:100%;border-collapse:collapse;margin:20px 0;}.med-table th,.med-table td{border:1px solid #ddd;padding:12px;text-align:left;}.med-table th{background:#f8fafc;}.footer{margin-top:40px;text-align:right;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(printContent.innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="prescriptions-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              E-Prescriptions
            </h1>
            <p className="text-slate-600 mt-1">Create and manage digital prescriptions</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 text-white rounded-lg px-6 btn-active" data-testid="create-prescription-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Prescription
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Create Prescription</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="patientName">Patient Name *</Label>
                    <Input
                      id="patientName"
                      placeholder="Enter patient name"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      data-testid="patient-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="patientAge">Age</Label>
                    <Input
                      id="patientAge"
                      type="number"
                      placeholder="Age"
                      value={patientAge}
                      onChange={(e) => setPatientAge(e.target.value)}
                      data-testid="patient-age-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="diagnosis">Diagnosis *</Label>
                  <Input
                    id="diagnosis"
                    placeholder="Enter diagnosis"
                    value={diagnosis}
                    onChange={(e) => setDiagnosis(e.target.value)}
                    data-testid="diagnosis-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="doctorName">Doctor Name *</Label>
                  <Input
                    id="doctorName"
                    placeholder="Dr. "
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    data-testid="doctor-name-input"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Medications</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addMedication}
                      data-testid="add-medication-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  {medications.map((med, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 items-end p-3 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-xs">Medicine</Label>
                        <Input
                          placeholder="Name"
                          value={med.name}
                          onChange={(e) => updateMedication(index, 'name', e.target.value)}
                          data-testid={`med-name-${index}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Dosage</Label>
                        <Input
                          placeholder="500mg"
                          value={med.dosage}
                          onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Frequency</Label>
                        <Input
                          placeholder="2x daily"
                          value={med.frequency}
                          onChange={(e) => updateMedication(index, 'frequency', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Duration</Label>
                        <Input
                          placeholder="7 days"
                          value={med.duration}
                          onChange={(e) => updateMedication(index, 'duration', e.target.value)}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeMedication(index)}
                        className="text-red-500"
                        disabled={medications.length === 1}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="instructions">Additional Instructions</Label>
                  <Textarea
                    id="instructions"
                    placeholder="Any special instructions..."
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    rows={3}
                    data-testid="instructions-input"
                  />
                </div>

                <Button
                  className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                  onClick={handleCreate}
                  disabled={creating}
                  data-testid="submit-prescription-btn"
                >
                  {creating ? 'Creating...' : 'Create Prescription'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Prescriptions List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton h-24 rounded-xl" />
            ))}
          </div>
        ) : prescriptions.length === 0 ? (
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="py-12 text-center">
              <FileSignature className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No prescriptions yet</p>
              <p className="text-sm text-slate-400 mt-1">Create your first e-prescription</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {prescriptions.map((prescription, index) => (
              <Card 
                key={prescription.id} 
                className="bg-white border-slate-200 shadow-sm card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s`, opacity: 0 }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FileSignature className="w-6 h-6 text-blue-900" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900">{prescription.patient_name}</h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span>{prescription.diagnosis}</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {prescription.prescription_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Pill className="w-3.5 h-3.5" />
                            {prescription.medications?.length || 0} medications
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewPrescription(prescription)}
                      data-testid={`view-prescription-${prescription.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Prescription Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            {selectedPrescription && (
              <>
                <DialogHeader>
                  <div className="flex items-center justify-between">
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Prescription Details</DialogTitle>
                    <Button variant="outline" size="sm" onClick={handlePrint}>
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </Button>
                  </div>
                </DialogHeader>
                
                <div ref={printRef} className="space-y-6 mt-4">
                  <div className="header text-center border-b-2 border-blue-900 pb-4">
                    <h2 className="text-xl font-bold text-blue-900">Medical Prescription</h2>
                    <p className="text-slate-600">{selectedPrescription.doctor_name}</p>
                    <p className="text-sm text-slate-500">{selectedPrescription.prescription_date}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Patient Name</p>
                      <p className="font-medium">{selectedPrescription.patient_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Age</p>
                      <p className="font-medium">{selectedPrescription.patient_age} years</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Diagnosis</p>
                      <p className="font-medium">{selectedPrescription.diagnosis}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3">Medications</h3>
                    <table className="med-table w-full border-collapse">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="border p-2 text-left">Medicine</th>
                          <th className="border p-2 text-left">Dosage</th>
                          <th className="border p-2 text-left">Frequency</th>
                          <th className="border p-2 text-left">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPrescription.medications?.map((med, index) => (
                          <tr key={index}>
                            <td className="border p-2">{med.name}</td>
                            <td className="border p-2">{med.dosage}</td>
                            <td className="border p-2">{med.frequency}</td>
                            <td className="border p-2">{med.duration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {selectedPrescription.instructions && (
                    <div>
                      <h3 className="font-semibold mb-2">Instructions</h3>
                      <p className="text-slate-700">{selectedPrescription.instructions}</p>
                    </div>
                  )}

                  <div className="footer text-right pt-6 border-t">
                    <p className="font-semibold">{selectedPrescription.doctor_name}</p>
                    <p className="text-sm text-slate-500">Signature</p>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
